import { ReactComponent as AppOnlyBadge } from '@assets/images/app/app-only-badge.svg';
import { ReactComponent as ChatPaymentBadge } from '@assets/images/app/chat-and-payment.svg';
import { ReactComponent as DirectPaymentBadge } from '@assets/images/app/direct-payment.svg';
import { ReactComponent as MembershipRibbon } from '@assets/images/app/membership-ribbon.svg';
import * as Skeleton from '@components/Skeleton/Skeleton';
import { PRODUCT_ACTIVITY_TYPE_TEXT, ProductStatus } from '@consts/products';
import { UserMembership } from '@models/common';
import { ProductModel } from '@models/product';
import { discountedAmountBy, getAppliedMembership } from '@utils/common';
import { getAddressText, getBadges } from '@utils/dataTransform';
import moment from 'moment';
import React from 'react';
import LinesEllipsis from 'react-lines-ellipsis';
import FavoriteButton from './FavoriteButton';

interface ProductListItemProps {
  item?: ProductModel;
  membership?: UserMembership;
  style?: any;
  hideBorderBottom?: boolean;
  hideBadges?: boolean;
  distance?: number;
  onClick?: (event: any) => void;
  onChangeFavorite?: (value: boolean) => void;
}

const IMAGE_WIDTH = '100px';

export const ProductListItem: React.FC<ProductListItemProps> = (props) => {
  const item = props.item;
  const userMembership = props.membership;
  const appOnly = item?.appOnly;
  const appliedMembership = getAppliedMembership(item, userMembership);

  const _renderItemImage = (imageUrl: string) => {
    const fixImageUrl = imageUrl;

    return (
      <div
        style={{
          flexShrink: 0,
          width: IMAGE_WIDTH,
          height: IMAGE_WIDTH,
          borderRadius: '8px',
          position: 'relative',
        }}>
        {appliedMembership && <MembershipRibbon style={{ position: 'absolute', left: 0, bottom: '8px' }} />}
        {appOnly && (
          <AppOnlyBadge
            style={{ position: 'absolute', left: 0, top: 0, borderTopLeftRadius: '8px', width: '40%', height: '40%' }}
          />
        )}
        {(item?.totalInventory === 0 || item?.soldout || item?.status === ProductStatus.CLOSE) && (
          <div
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '8px',
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: 0,
              left: 0,
              backgroundColor: 'rgba(255,255,255,0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <div
              style={{
                backgroundColor: '#242424',
                color: 'white',
                fontWeight: 700,
                fontFamily: 'Pretendard',
                fontSize: '12px',
                textAlign: 'center',
                padding: '0 8px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
              }}>
              {item?.closeText || '판매 종료'}
            </div>
          </div>
        )}
        <Skeleton.Image
          width={IMAGE_WIDTH}
          height={IMAGE_WIDTH}
          src={fixImageUrl}
          styles={{ image: { borderRadius: '8px', objectFit: 'cover' } }}
        />
        <div
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            position: 'absolute',
            width: IMAGE_WIDTH,
            height: IMAGE_WIDTH,
            border: '1px solid black',
            opacity: 0.04,
            borderRadius: '8px',
          }}
        />
        <div
          style={{
            position: 'absolute',
            borderTopLeftRadius: '3px',
            bottom: 0,
          }}></div>
        <div
          style={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            borderBottomLeftRadius: '3px',
          }}></div>
      </div>
    );
  };

  const addressText = getAddressText({
    activityType: item?.activityType,
    availableArea: item?.location,
  });

  const mainImageUrl = item?.thumbnailImage as string;
  const badges = props && item && item?.badges && item?.badges.length > 0 ? item?.badges : [];

  let isNew = false;
  const firstApprovedAt = props && item && item?.approvedAt ? item?.approvedAt : new Date(1990, 0, 1);
  const monthCheck = moment(new Date()).diff(moment(firstApprovedAt), 'day');
  if (monthCheck <= 30) {
    isNew = true;
  }

  const activeBadgeList = getBadges({
    badges: badges,
    isNew,
    isClosed: item?.status === ProductStatus.CLOSE,
    applyGroupDiscount: item?.applyGroupDiscount,
  });

  if (item?.applyPeriodDiscount && item?.discountInfo?.discountedFee) {
    const today = new Date();
    if (
      item?.discountInfo?.discountStartDate &&
      item?.discountInfo?.discountEndDate &&
      new Date(item?.discountInfo?.discountStartDate) < today &&
      new Date(item?.discountInfo?.discountEndDate) > today
    ) {
      item.fee = item?.discountInfo?.discountedFee || 0;
      item.discount = `${Math.floor(100 - (item.fee / item?.originFee) * 100)}%`;
    }
  }

  let amount = item?.fee || 0;
  const originAmount = item?.originFee || amount;
  if (item && appliedMembership?.hideDiscountPercent) {
    amount = discountedAmountBy(item?.fee || 0, appliedMembership?.discountPercent || 0);
    item.discount = `${Math.floor(100 - (amount / originAmount) * 100)}%`;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        padding: '16px',
        ...props.style,
      }}>
      <div
        onClick={props.onClick}
        style={{
          cursor: 'pointer',
          width: '100%',
          height: '100%',
        }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            width: '100%',
          }}>
          {mainImageUrl && _renderItemImage(mainImageUrl)}
          <div
            style={{
              width: `calc(100% - ${IMAGE_WIDTH} - 32px)`,
              padding: '0 12px',
            }}>
            {item?.inventory !== undefined && item?.inventory > 0 && (
              <div
                style={{
                  color: item?.inventory > 5 ? '#1890FF' : '#FC4242',
                  fontFamily: 'Pretendard',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  marginBottom: '4px',
                }}>
                {item?.inventory > 5 ? '그룹신청 가능' : '품절임박'}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  flexShrink: 0,
                  gap: '4px',
                  width: '100%',
                  minWidth: 0,
                }}>
                {item?.activityType && (
                  <>
                    <div
                      style={{
                        fontSize: '12px',
                        textAlign: 'center',
                        color: '#808387',
                        fontWeight: 'bold',
                        flexShrink: 0,
                      }}>
                      {PRODUCT_ACTIVITY_TYPE_TEXT[item?.activityType]?.title}
                    </div>
                    <div
                      style={{
                        width: '1px',
                        height: '8px',
                        backgroundColor: '#d8d8d8',
                      }}
                    />
                  </>
                )}
                {addressText && addressText !== '-' && (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexShrink: 0,
                      }}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}>
                        <div>
                          <div
                            style={{
                              fontSize: '12px',
                              textAlign: 'center',
                              color: '#808387',
                            }}>
                            {addressText}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        width: '1px',
                        height: '8px',
                        backgroundColor: '#d8d8d8',
                      }}
                    />
                  </>
                )}
                {props.distance !== undefined && (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexShrink: 0,
                      }}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}>
                        <div>
                          <div
                            style={{
                              fontSize: '12px',
                              textAlign: 'center',
                              color: '#808387',
                            }}>
                            {`${(props.distance / 1000).toFixed(1)}km`}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        width: '1px',
                        height: '8px',
                        backgroundColor: '#d8d8d8',
                      }}
                    />
                  </>
                )}
                <div
                  style={{
                    fontSize: '12px',
                    color: '#808387',
                    wordBreak: 'break-word',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}>
                  {item && item?.sellerName && item?.sellerName !== '' ? item?.sellerName : ''}
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: '6px',
                fontSize: '14px',
                color: '#424242',
                lineHeight: '19px',
                maxHeight: '38px',
              }}>
              <LinesEllipsis text={item?.title} maxLine={2} ellipsis="..." trimRight></LinesEllipsis>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginTop: '6px',
                height: '18px',
                alignItems: 'center',
              }}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                {item?.discount && item.discount.replace(/[^0-9]/g, '') !== '' && parseInt(item.discount) > 0 && (
                  <div
                    style={{
                      color: '#F03F45',
                      fontFamily: 'SF Pro Display',
                      fontSize: '13px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                    }}>
                    {item?.discount}
                  </div>
                )}
                <div
                  style={{
                    fontWeight: 'bold',
                    fontSize: '13px',
                    color: item?.soldout ? '#9b9da0' : '#242424',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                  <span>{amount.toLocaleString()}원</span>
                  {item?.saleType === 'class' && <ChatPaymentBadge />}
                </div>

                {item?.saleType === 'commerce' && (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <DirectPaymentBadge />
                  </div>
                )}
              </div>
            </div>
            {appliedMembership && (
              <div
                style={{
                  color: 'var(--Semantic-Negative-100, #FC4242)',
                  fontFamily: 'Pretendard',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  marginTop: '4px',
                }}>
                {appliedMembership.hideDiscountPercent
                  ? `멤버십 혜택가`
                  : `멤버십 할인가 ${discountedAmountBy(
                      item?.fee || 0,
                      appliedMembership?.discountPercent || 0,
                    ).toLocaleString()}원`}
              </div>
            )}
            {item?.saleType === 'class' && activeBadgeList?.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginTop: '4px',
                }}>
                {activeBadgeList.map((item) => {
                  return (
                    <div
                      key={`key-${item?.type}`}
                      style={{
                        display: 'flex',
                        marginRight: '4px',
                        paddingLeft: '4px',
                        paddingRight: '4px',
                        height: '18px',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        backgroundColor: item?.backgroundColor,
                      }}>
                      <div
                        style={{
                          fontSize: '11px',
                          color: item?.color,
                          fontWeight: 500,
                        }}>
                        {item?.value}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {item?.soldout && (
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px',
                    height: '16px',
                    backgroundColor: '#808387',
                    borderRadius: '4px',
                    fontSize: '9px',
                    fontWeight: 'bold',
                    color: 'white',
                  }}>
                  SOLD OUT
                </div>
              </div>
            )}
          </div>
          <FavoriteButton productId={item?._id} saleType={'commerce'} />
        </div>
      </div>
    </div>
  );
};
